import React, { useMemo } from 'react';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import { Grid, Paper, Typography } from '@mui/material';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import { addCarrierSettings, updateCarrierSettings } from '@Reducers/User/actions';
import SbButton from '@Components/Button';
import Divider from '@Components/Divider';
import * as Yup from 'yup';
import { emiratesCarrier } from './Type';
import { setCarrierSettings } from '@Reducers/User';
import SBCountryDropdown from '@Components/Dropdown/Country';
import {
  paymentModeOptions,
  serviceTypeOptions,
  shipmentPurposeOptions,
  shipmentTypeOptions,
} from '../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/Emirates/constants';

interface propsTypes {
  onClose: () => void;
  isEditable: boolean;
}

// ... validation schema from above ...
const emiratesValidationSchema = Yup.object().shape({
  accountNo: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Account value is required'),
        display: Yup.string().required('Account display is required'),
      }),
    )
    .min(1, 'At least one account number is required')
    .required('Account number is required')
    .default([]),

  serviceType: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Service Type value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Service Type is required')
    .required('Service Type is required')
    .default([]),

  paymentMode: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Payment mode value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Payment mode is required')
    .required('Payment mode is required')
    .default([]),
  shipmentPurpose: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Shipment purpose value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Shipment purpose is required')
    .required('Shipment purpose is required')
    .default([]),
  shipmentType: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Shipment type value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Shipment type is required')
    .required('Shipment type is required')
    .default([]),
  countryOfManufacture: Yup.string().required('Country of manufacture is required'),
});

const Emirates = (props: propsTypes) => {
  const { onClose, isEditable } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { vendersAccount } = useSelector((state: RootStateType) => state.adminShipment);
  const { userDataById, carrierSetting } = useSelector((state: RootStateType) => state.user);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })),
    [vendersAccount],
  );

  const { values, setFieldValue, errors, handleSubmit } = useFormik<emiratesCarrier>({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: emiratesValidationSchema,
    initialValues: {
      ...carrierSetting.emirates,
    },
    onSubmit(values) {
      // ✅ Remove DG flags from values
      const {
        isDG,
        isDGaccessible,
        isConsigneeAddressResidential,
        isShipperAddressResidential,
        shipperId,
        ...filteredValues
      } = values;

      // ✅ Remove DG flags from carrierSetting.emirates
      const {
        isDG: carrierIsDG,
        isDGaccessible: carrierIsDGaccessible,
        isConsigneeAddressResidential: carrierIsConsigneeAddressResidential,
        isShipperAddressResidential: carrierIsShipperAddressResidential,
        shipperId: carrierShipperId,
        ...filteredCarrierEmirates
      } = carrierSetting.emirates;

      const finalPayload = {
        ...filteredCarrierEmirates,
        ...filteredValues,
      };

      if (userDataById?._id) {
        if (carrierSetting.emirates._id) {
          dispatch(updateCarrierSettings(finalPayload));
        } else {
          dispatch(addCarrierSettings(finalPayload));
        }
      } else {
        dispatch(
          setCarrierSettings({
            values: finalPayload,
            carrierType: 'emirates',
          }),
        );
      }

      onClose();
    },
  });

  console.info('carrierSetting.emirates', carrierSetting.emirates);

  return (
    <div>
      <>
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">Emirates Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label={`Emirates Account`}
                    required
                    multiple={true}
                    placeholder="Select"
                    name="accountNo"
                    value={values?.accountNo?.map(item => item.value)}
                    options={accountOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={Array.isArray(errors.accountNo) ? errors.accountNo.join(', ') : errors.accountNo}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBCountryDropdown
                    disabled={!isEditable}
                    required
                    label="Country of Manufacture"
                    value={values?.countryOfManufacture}
                    options={undefined}
                    onChange={value => {
                      setFieldValue('countryOfManufacture', value[0]);
                    }}
                    error={errors?.countryOfManufacture as string}
                  />
                </Grid>
              </Grid>

              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="serviceType"
                    label={`Service Type`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="serviceType"
                    value={values?.serviceType?.map(item => item.value)}
                    options={serviceTypeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('serviceType', selectedObj);
                    }}
                    error={errors.serviceType as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="paymentMode"
                    label={`Payment Mode`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="paymentMode"
                    value={values?.paymentMode?.map(item => item.value)}
                    options={paymentModeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('paymentMode', selectedObj);
                    }}
                    error={errors.paymentMode as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="shipmentPurpose"
                    label={`Shipment Purpose`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="shipmentPurpose"
                    value={values?.shipmentPurpose?.map(item => item.value)}
                    options={shipmentPurposeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('shipmentPurpose', selectedObj);
                    }}
                    error={errors.shipmentPurpose as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="shipmentType"
                    label={`Shipment Type`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="shipmentType"
                    value={values?.shipmentType?.map(item => item.value)}
                    options={shipmentTypeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('shipmentType', selectedObj);
                    }}
                    error={errors.shipmentType as string}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      type="submit"
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: `4px !important` }}
                      className="w-full"
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      Save
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        type="button"
                        sx={{ borderRadius: `4px !important` }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </>
    </div>
  );
};

export default Emirates;
